import React, {
    createContext, useMemo, useState, FC, ReactNode, Dispatch, SetStateAction,
} from 'react';
import LoadingOverlay from 'react-loading-overlay-nextgen';
import { useHeader } from './header/useHeader';
import { Loader } from '../../ui-library';

// 1. Define the shape of the Header State (adjust based on useHeader's actual return)
interface HeaderState {
    clientName?: string | null;
    dfsClientId?: string | number | null;
    isDataAccessible?: boolean;
    // ... add other properties from your headerState
}

// 2. Define the Context interface
interface LayoutContextInterface {
    headerState: HeaderState;
    initHeaderState: (state: any) => void; // Replace 'any' with your specific state interface if available
    changeHeaderState: (state: Partial<any>) => void;
    setLoadingOverlayActive: Dispatch<SetStateAction<boolean>>;
}

// 3. Initialize Context with a default value or undefined
// We use 'as LayoutContextInterface' to avoid null-checks everywhere later,
// provided we guarantee the Provider wraps the app.
export const LayoutContext = createContext<LayoutContextInterface>({} as LayoutContextInterface);

interface LayoutProviderProps {
    children: ReactNode;
}

export const LayoutProvider: FC<LayoutProviderProps> = ({ children }) => {
    const [isLoadingOverlayActive, setLoadingOverlayActive] = useState<boolean>(false);

    // Typing for useHeader is ideally handled inside its own hook file,
    // but the types flow through here.
    const { headerState, initHeaderState, changeHeaderState } = useHeader();

    const value = useMemo(() => ({
        headerState,
        initHeaderState,
        changeHeaderState,
        setLoadingOverlayActive,
    }), [headerState, initHeaderState, changeHeaderState]);

    return (
        <LayoutContext.Provider value={value}>
            <LoadingOverlay
                active={isLoadingOverlayActive}
                // @ts-ignore - Some older overlay libs have conflicting spinner types
                spinner={<Loader large />}
            >
                {children}
            </LoadingOverlay>
        </LayoutContext.Provider>
    );
};
